import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, RefreshCw, Clock, ChevronRight, TrendingUp, AlertCircle, History, Zap, Settings, Trash2, Plus, X, Eye } from 'lucide-react';
import styled, { keyframes, css } from 'styled-components';
import apiService from '../../services/apiService';
import { isMarketClosed } from '../../utils/marketCalendar';

// ─── Animations ──────────────────────────────────────────
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const glow = keyframes`0%,100%{box-shadow:0 0 6px #06b6d4}50%{box-shadow:0 0 18px #06b6d4,0 0 30px #0e7490}`;

// ─── Styled Components ────────────────────────────────────
const PageWrap = styled.div`
  min-height:100vh;background:#0a0d12;color:#e2e8f0;font-family:'Inter',sans-serif;padding:24px;
`;
const Header = styled.div`
  display:flex;justify-content:space-between;align-items:center;margin-bottom:28px;flex-wrap:wrap;gap:12px;
`;
const Title = styled.h1`
  font-size:24px;font-weight:700;color:#fff;display:flex;align-items:center;gap:10px;
  .badge{font-size:11px;background:linear-gradient(135deg,#06b6d4,#0369a1);padding:3px 8px;border-radius:12px;font-weight:600;letter-spacing:.5px;}
`;
const Controls = styled.div`display:flex;gap:10px;align-items:center;flex-wrap:wrap;`;
const Btn = styled.button`
  display:flex;align-items:center;gap:6px;padding:9px 16px;border-radius:8px;border:none;
  cursor:pointer;font-size:13px;font-weight:600;transition:all .2s;
  ${p=>p.primary?css`background:linear-gradient(135deg,#06b6d4,#0369a1);color:#fff;&:hover{opacity:.85}`
    :css`background:rgba(255,255,255,.06);color:#94a3b8;border:1px solid rgba(255,255,255,.1);&:hover{background:rgba(255,255,255,.1);color:#e2e8f0}`}
`;
const MarketClosedBadge = styled.div`
  display:flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;
  background:rgba(234,179,8,.08);border:1px solid rgba(234,179,8,.25);color:#eab308;font-size:12px;font-weight:600;
`;
const LiveDot = styled.div`
  width:8px;height:8px;border-radius:50%;background:#06b6d4;
  animation:${glow} 2s ease-in-out infinite;
`;
const StatusBar = styled.div`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;
  padding:12px 16px;margin-bottom:20px;display:flex;align-items:center;gap:12px;font-size:13px;color:#64748b;flex-wrap:wrap;
`;
const Grid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:20px;@media(max-width:720px){grid-template-columns:1fr}`;
const Card = styled.div`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:14px;padding:20px;
  animation:${fadeIn} .4s ease;
`;
const CardTitle = styled.h2`font-size:14px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:.8px;margin-bottom:16px;display:flex;align-items:center;gap:8px;`;
const PivotItem = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:12px 14px;border-radius:10px;margin-bottom:8px;cursor:pointer;transition:all .2s;
  background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.05);
  &:hover{background:rgba(6,182,212,.08);border-color:rgba(6,182,212,.25);}
`;
const StatusBadge = styled.span`
  font-size:10px;font-weight:700;padding:3px 7px;border-radius:5px;letter-spacing:.5px;
  ${p=>p.status==='BREAKOUT'?css`background:rgba(239,68,68,.15);color:#ef4444;border:1px solid rgba(239,68,68,.25)`
    :p.status==='NEAR_BREAKOUT'?css`background:rgba(234,179,8,.12);color:#eab308;border:1px solid rgba(234,179,8,.2)`
    :css`background:rgba(99,102,241,.1);color:#6366f1;border:1px solid rgba(99,102,241,.2)`}
`;
const ImprovTag = styled.span`
  font-size:10px;padding:2px 6px;border-radius:4px;
  background:rgba(6,182,212,.15);color:#67e8f9;border:1px solid rgba(6,182,212,.2);margin-left:6px;
`;
const LeaderRow = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:9px 0;border-bottom:1px solid rgba(255,255,255,.05);font-size:13px;
  &:last-child{border-bottom:none}
`;
const GradeBadge = styled.span`
  font-size:11px;font-weight:700;padding:2px 7px;border-radius:5px;
  ${p=>p.grade==='S'?css`background:rgba(239,68,68,.15);color:#ef4444`
    :p.grade==='A'?css`background:rgba(234,179,8,.12);color:#eab308`
    :p.grade==='B'?css`background:rgba(99,102,241,.1);color:#818cf8`
    :css`background:rgba(255,255,255,.06);color:#64748b`}
`;
const Empty = styled.div`text-align:center;padding:40px;color:#334155;font-size:14px;`;
const LoadingDot = styled.div`animation:${pulse} 1.4s ease-in-out infinite;text-align:center;padding:40px;color:#06b6d4;font-size:28px;`;
const DiffTag = styled.span`
  font-size:10px;padding:2px 6px;border-radius:4px;background:rgba(52,211,153,.1);color:#34d399;
  border:1px solid rgba(52,211,153,.2);margin-left:4px;
`;
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center;
  z-index: 1000; backdrop-filter: blur(5px);
`;
const Modal = styled.div`
  background: #0a0d12; border: 1px solid rgba(255,255,255,.1); border-radius: 16px;
  width: 90%; max-width: 400px; padding: 24px; color: #fff;
`;

export default function LivermoreV21Page() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [marketClosed] = useState(isMarketClosed(new Date()));

    const [schedulerConfig, setSchedulerConfig] = useState({
        enabled: true, times: ['09:30', '13:00', '15:00'], weekdaysOnly: true
    });
    const [showSettings, setShowSettings] = useState(false);
    const [newTime, setNewTime] = useState('09:00');

    const isExecutingRef = useRef(false);

    const load = useCallback(async (force = false) => {
        setLoading(true); setError(null);
        try {
            const res = await apiService.getLivermoreV21Leaders(force);
            setData(res);
            setLastUpdate(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchScheduler = useCallback(async () => {
        try {
            const res = await apiService.getLivermoreV21Scheduler();
            if (res && res.config) setSchedulerConfig(res.config);
        } catch (e) { console.error('Failed to fetch V21 scheduler', e); }
    }, []);

    useEffect(() => {
        load();
        fetchScheduler();
    }, [load, fetchScheduler]);

    // 스케줄러 핵심 로직 (10초 주기 체크)
    const runAutoSaveProcedure = useCallback(async (config) => {
        if (!config || !config.enabled || !config.times || config.times.length === 0) return;
        const now = new Date();
        const hour = now.getHours();
        const minute = now.getMinutes();

        const kstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
        const todayStr = kstNow.toISOString().split('T')[0];

        const currentMins = hour * 60 + minute;
        const storageKey = 'livermore_v21_processed_slots';

        try {
            for (const timeStr of config.times) {
                const [sh, sm] = timeStr.split(':').map(Number);
                const slotMins = sh * 60 + sm;

                if (currentMins >= slotMins && currentMins < slotMins + 30) {
                    let processedData = { date: '', slots: {} };
                    try {
                        const stored = localStorage.getItem(storageKey);
                        if (stored) processedData = JSON.parse(stored);
                    } catch (e) {}

                    if (processedData.date === todayStr && processedData.slots[timeStr]) continue;

                    if (isExecutingRef.current) return;
                    console.log(`[V21 Scheduler] 🎯 Triggering check for ${timeStr} slot...`);
                    isExecutingRef.current = true;

                    const history = await apiService.getLivermoreV21History();
                    const alreadyExists = history.some(h => {
                        const hDate = new Date(h.createdAt);
                        const hTimeMins = hDate.getHours() * 60 + hDate.getMinutes();
                        const hKstDate = new Date(hDate.getTime() + 9 * 60 * 60 * 1000);
                        const hDateStr = hKstDate.toISOString().split('T')[0];
                        return hDateStr === todayStr && Math.abs(hTimeMins - slotMins) < 15;
                    });

                    if (!alreadyExists) {
                        console.log(`[V21 Scheduler] Saving snapshot for ${timeStr}...`);
                        await apiService.saveLivermoreV21Snapshot();
                        await load();
                    }

                    processedData.date = todayStr;
                    processedData.slots[timeStr] = true;
                    localStorage.setItem(storageKey, JSON.stringify(processedData));
                    isExecutingRef.current = false;
                }
            }
        } catch (e) {
            console.error('[V21 Scheduler] Error:', e);
            isExecutingRef.current = false;
        }
    }, [load]);

    useEffect(() => {
        if (!schedulerConfig || !schedulerConfig.enabled) return;
        const intervalId = setInterval(() => {
            runAutoSaveProcedure(schedulerConfig);
        }, 10000);
        return () => clearInterval(intervalId);
    }, [schedulerConfig, runAutoSaveProcedure]);

    const handleUpdateScheduler = async (updates) => {
        try {
            const res = await apiService.updateLivermoreV21Scheduler({ ...schedulerConfig, ...updates });
            if (res.success) {
                setSchedulerConfig(res.status.config);
                runAutoSaveProcedure(res.status.config);
            }
        } catch (err) {
            alert(`스케줄러 업데이트 실패: ${err.message}`);
        }
    };

    const saveSnapshot = async () => {
        try {
            setLoading(true);
            await apiService.saveLivermoreV21Snapshot();
            await load(true);
        } catch (e) {
            setError(e.message);
            setLoading(false);
        }
    };

    return (
        <PageWrap>
            <Header>
                <Title>
                    <Activity size={22} color="#06b6d4" />
                    리버모어 스캐너
                    <span className="badge">V2.1</span>
                </Title>
                <Controls>
                    {marketClosed && (
                        <MarketClosedBadge>
                            <AlertCircle size={14} />MARKET CLOSED
                        </MarketClosedBadge>
                    )}
                    <Btn onClick={() => setShowSettings(true)}>
                        <Settings size={14} />설정
                        {schedulerConfig?.enabled && (
                            <span style={{
                                width: 6, height: 6, background: '#06b6d4', borderRadius: '50%', marginLeft: 4,
                                boxShadow: '0 0 8px #06b6d4'
                            }} />
                        )}
                    </Btn>
                    <Btn onClick={() => navigate('/labs/livermore-v21/history')}>
                        <History size={14} />히스토리
                    </Btn>
                    <Btn onClick={() => load(true)}>
                        <RefreshCw size={14} />새로고침
                    </Btn>
                    <Btn primary onClick={saveSnapshot}>
                        <Zap size={14} />스냅샷 저장
                    </Btn>
                </Controls>
            </Header>

            {/* V2.1 개선 요약 */}
            <StatusBar>
                <LiveDot />
                <span style={{ color: '#94a3b8' }}>V2.1 헛점 보강:</span>
                <ImprovTag>52W fallback 제거</ImprovTag>
                <ImprovTag>임계값 95% 일치</ImprovTag>
                <ImprovTag>뉴스 단어경계</ImprovTag>
                <ImprovTag>ETF prefix 매칭</ImprovTag>
                <ImprovTag>우선주 코드 차단</ImprovTag>
                <ImprovTag>BREAKOUT-only 후보</ImprovTag>
                {lastUpdate && <span style={{ marginLeft: 'auto', color: '#475569', fontSize: '12px' }}>
                    <Clock size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {lastUpdate.toLocaleTimeString('ko-KR')}
                </span>}
            </StatusBar>

            {showSettings && schedulerConfig && (
                <ModalOverlay onClick={() => setShowSettings(false)}>
                    <Modal onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <h2 style={{ fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={18} color="#06b6d4" /> 스케줄러 설정</h2>
                            <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '14px' }}>
                                <input
                                    type="checkbox"
                                    checked={schedulerConfig.enabled}
                                    onChange={e => handleUpdateScheduler({ enabled: e.target.checked })}
                                />
                                자동 판별 및 스냅샷 저장 켜기
                            </label>
                            <p style={{ fontSize: '11px', color: '#64748b', marginTop: 8 }}>
                                페이지를 띄워두시면 지정한 시간에 V2.1 기준으로 자동 판별하여 히스토리에 기록합니다.
                            </p>
                        </div>

                        <div style={{ marginBottom: 12, fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>실행 시간 목록</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {schedulerConfig.times.map(time => (
                                <div key={time} style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(255,255,255,.03)', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,.05)' }}>
                                    <span>{time}</span>
                                    <button
                                        onClick={() => handleUpdateScheduler({ times: schedulerConfig.times.filter(t => t !== time) })}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                            <input
                                type="time"
                                value={newTime}
                                onChange={e => setNewTime(e.target.value)}
                                style={{ flex: 1, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: '#fff', padding: '10px', borderRadius: 8 }}
                            />
                            <button
                                onClick={() => {
                                    if (!schedulerConfig.times.includes(newTime)) {
                                        handleUpdateScheduler({ times: [...schedulerConfig.times, newTime].sort() });
                                    }
                                }}
                                style={{ background: '#06b6d4', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    </Modal>
                </ModalOverlay>
            )}

            {error && (
                <Card style={{ borderColor: 'rgba(239,68,68,.2)', marginBottom: 20 }}>
                    <span style={{ color: '#ef4444' }}>⚠️ {error}</span>
                </Card>
            )}

            {loading ? (
                <LoadingDot>◉ 분석 중...</LoadingDot>
            ) : data && (
                <>
                    <Grid>
                        {/* 전환점 리스트 */}
                        <Card>
                            <CardTitle>
                                <TrendingUp size={15} color="#06b6d4" />
                                전환점 탐지
                                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#334155' }}>
                                    {data.pivotPoints?.length || 0}개
                                </span>
                            </CardTitle>
                            {(!data.pivotPoints || data.pivotPoints.length === 0) ? (
                                <Empty>탐지된 전환점이 없습니다</Empty>
                            ) : data.pivotPoints.map((p, i) => (
                                <PivotItem key={p.code} style={{ animationDelay: `${i * 40}ms` }} onClick={() => window.open(`/labs/stock/technical?symbol=${p.code}${p.resistanceLine ? `&pivot=${p.resistanceLine}` : ''}`, '_blank')}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>{p.name}</span>
                                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>{p.price?.toLocaleString()}원</span>
                                            <StatusBadge status={p.pivotStatus}>{p.pivotStatus}</StatusBadge>
                                            {p.isTrendStarter && (
                                                <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    👑장기추세 초입
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#475569' }}>
                                            Vol:{p.volSurge?.toFixed(1)}x · 변동:{p.volatility}
                                            {p.trValueFormatted && ` · 대금:${p.trValueFormatted} · 시총:${p.mcapFormatted}`}
                                            {p.targetUpside && <span style={{ marginLeft: 8, color: '#10b981', fontWeight: 600 }}>🎯상승여력: {p.targetUpside}</span>}
                                            {p.closePosition && <> · 종가위치:<DiffTag>{p.closePosition}</DiffTag></>}
                                            {p.highProximity && <> · 52W고가:<DiffTag>{p.highProximity}</DiffTag></>}
                                        </div>
                                    </div>
                                    <ChevronRight size={16} color="#334155" />
                                </PivotItem>
                            ))}
                        </Card>

                        {/* 주도주 리스트 */}
                        <Card>
                            <CardTitle>
                                <Activity size={15} color="#06b6d4" />
                                주도주 스캐너
                                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#334155' }}>
                                    {data.leaders?.length || 0}개
                                </span>
                            </CardTitle>
                            {(!data.leaders || data.leaders.length === 0) ? (
                                <Empty>데이터 없음</Empty>
                            ) : data.leaders.slice(0, 15).map(l => (
                                <LeaderRow key={l.code} onClick={() => window.open(`/labs/stock/technical?symbol=${l.code}`, '_blank')} style={{ cursor: 'pointer' }}>
                                    <div>
                                        <span style={{ fontWeight: 600, color: '#e2e8f0', marginRight: 8 }}>{l.name}</span>
                                        <GradeBadge grade={l.grade}>{l.grade}</GradeBadge>
                                        {l.isTrendStarter && (
                                            <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', marginLeft: '6px' }}>
                                                👑장기추세
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ textAlign: 'right', fontSize: '12px' }}>
                                        <div style={{ color: l.changeRate >= 0 ? '#ef4444' : '#3b82f6', fontWeight: 600 }}>
                                            {l.changeRate >= 0 ? '+' : ''}{l.changeRate?.toFixed(2)}%
                                        </div>
                                        <div style={{ color: '#334155' }}>
                                            52W: <span style={{ color: '#06b6d4' }}>{l.highProximity}</span>
                                        </div>
                                    </div>
                                </LeaderRow>
                            ))}
                        </Card>
                    </Grid>

                    {/* Watchlist (FORMING_BASE) */}
                    {data.watchlist && data.watchlist.length > 0 && (
                        <Card style={{ marginTop: 20 }}>
                            <CardTitle>
                                <Eye size={15} color="#6366f1" />
                                관찰 후보 (Forming Base)
                                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#334155' }}>
                                    {data.watchlist.length}개
                                </span>
                            </CardTitle>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 8 }}>
                                {data.watchlist.map(w => (
                                    <div key={w.code} onClick={() => window.open(`/labs/stock/technical?symbol=${w.code}`, '_blank')} style={{
                                        padding: '10px 12px', borderRadius: 8,
                                        background: 'rgba(99,102,241,.04)', border: '1px solid rgba(99,102,241,.12)',
                                        fontSize: 12, cursor: 'pointer'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600, color: '#e2e8f0' }}>{w.name} {w.isTrendStarter && <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 4px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', verticalAlign: 'text-top' }}>👑초입</span>}</span>
                                            <StatusBadge status={w.pivotStatus}>{w.pivotStatus}</StatusBadge>
                                        </div>
                                        <div style={{ color: '#475569', fontSize: 11 }}>
                                            {w.price?.toLocaleString()}원 · 변동 {w.volatility} · 52W {w.highProximity}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </>
            )}
        </PageWrap>
    );
}
